import dayjs, { type Dayjs } from 'dayjs';

// calendar_seasons: a school year, 1 September – 31 August, with its terms.
export interface CalendarSeason {
  id: number;
  name: string;
  start_year: number;
  from: string;
  to: string;
  terms: CalendarTerm[];
}

export interface CalendarTerm {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
}

// calendar_kinds: an Entry kind (CONTEXT.md).
export interface CalendarKind {
  id: number;
  name: string;
  color: string;
  no_lessons: boolean;
  // Its entries are entered with a start and end time.
  has_time: boolean;
  sort_order: number | null;
}

// calendar_entries: a Calendar entry (CONTEXT.md).
export interface CalendarEntry {
  id: number;
  title: string;
  kind_id: number;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  description: string | null;
}

// What the entry drawer opens with: an entry, or new dates to create one on.
export type EntryDraft = { entry: CalendarEntry } | { startDate: string; endDate: string };

export const DATE = 'YYYY-MM-DD';

export const WEEKDAYS = ['Да', 'Мя', 'Лх', 'Пү', 'Ба', 'Бя', 'Ня'];

export const monthName = (d: Dayjs) => `${d.month() + 1}-р сар`;

// Monday-first weekday index: Monday 0 … Sunday 6.
export const weekdayIndex = (d: Dayjs) => (d.day() + 6) % 7;

export const isWeekend = (d: Dayjs) => weekdayIndex(d) >= 5;

// The 6 Monday-first weeks shown for a month.
export function monthGrid(month: Dayjs): Dayjs[][] {
  const first = month.startOf('month');
  const start = first.subtract(weekdayIndex(first), 'day');
  return Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => start.add(w * 7 + d, 'day')),
  );
}

// The season window (1 Sep – 31 Aug) a date falls in.
export function seasonWindowOf(d: Dayjs) {
  const startYear = d.month() >= 8 ? d.year() : d.year() - 1;
  return { startYear, from: dayjs(`${startYear}-09-01`), to: dayjs(`${startYear + 1}-08-31`) };
}

export const covers = (e: { start_date: string; end_date: string }, date: string) =>
  e.start_date <= date && date <= e.end_date;

// The entry's dates for people: "10.15", "10.15 – 10.17", with times.
export function entryWhen(e: CalendarEntry): string {
  const s = dayjs(e.start_date);
  const end = dayjs(e.end_date);
  const fmt = (d: Dayjs) => d.format(d.year() === dayjs().year() ? 'MM.DD' : 'YYYY.MM.DD');
  const dates = e.start_date === e.end_date ? fmt(s) : `${fmt(s)} – ${fmt(end)}`;
  if (!e.start_time) return dates;
  return `${dates}, ${e.start_time}${e.end_time ? `–${e.end_time}` : ''}`;
}

// A hex colour with alpha, for tints.
export const tint = (hex: string, alpha: string) => `${hex}${alpha}`;
